import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import scrollIntoView from 'dom-scroll-into-view';
import SelectableListOption from './SelectableListOption';
import * as UuIdUtils from '../../utils/UuIdGenerator';
import styles from 'styles/partials/_selectable-list.css';
import ContextButtonContext from '../Context/ContextButtonContext';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import { AlertType } from '../../graphql/schema/p4types';
import { Tooltip } from '@fluentui/react-components';

//Not sure of how best to do this - We want both state updates to trigger for a value, AND for it to be mutable so when accessed by listener functions it gets the most up to date value.
//Figured a quick wrapper to use move approaches would do for now.
const useMutableState = (initialValue) => {
  const mutable = useRef(initialValue);
  const [value, setValue] = useState(initialValue);

  return [
    value,
    (newValue) => {
      mutable.current = newValue;
      setValue(newValue);
    },
    mutable,
  ];
};

const SelectableList = (props) => {
  const dataid = useRef(UuIdUtils.uuIdGenerator());

  const optionsContainerRef = useRef();
  const optionsRefs = useRef({});

  const keyHandlerId = useRef(null);

  const dispatch = useDispatch();

  //Note: highlightedIndex should === currentHighlightedIndex. Just the latter is mutable and hence usable in functions defined only once.
  const [highlightedIndex, setHighlightedIndex, highlightedIndexRef] = useMutableState(null);
  const [showHighlight, setShowHighlight] = useState(false);

  const { addKeyDownHandler, removeKeyDownHandler } = useContext(ContextButtonContext);

  useEffect(() => {
    if (highlightedIndex >= props.options.length) {
      setHighlightedIndex(props.options.length - 1);
    }
  }, [props.options.length]);

  useEffect(() => {
    // Register keyDown handler with parent container as parent rather than this list will generally have
    // focus while processing keyboard events relating to this list
    if (addKeyDownHandler && removeKeyDownHandler) {
      keyHandlerId.current = addKeyDownHandler(handleKeyDown);
    }

    return () => {
      // Remove keyDown handler if it was added to parent
      if (keyHandlerId.current) {
        removeKeyDownHandler(keyHandlerId.current);
      }
    };
  }, []);

  //Scroll Option into View.
  useEffect(() => {
    if (props.showHighlight && highlightedIndex !== null) {
      const optionNode = ReactDOM.findDOMNode(optionsRefs.current[`option-${highlightedIndex}`]);
      if (!optionNode) {
        return;
      }
      const containerNode = optionsContainerRef.current;

      scrollIntoView(optionNode, containerNode, { onlyScrollIfNeeded: true });
    }
  }, [props.showHighlight, highlightedIndex, optionsContainerRef.current]);

  // Down arrow key event handler - Move down to next item in list or to top of list if currently at bottom
  const keyArrowDown = (event) => {
    const index =
      highlightedIndexRef.current === null || highlightedIndexRef.current === props.options.length - 1
        ? 0
        : highlightedIndexRef.current + 1;
    setHighlightedIndex(index);
    setShowHighlight(true);
  };

  // Up arrow key event handler - Move up to previous item in list or to bottom of list if currently at top
  const keyArrowUp = (event) => {
    const index = !highlightedIndexRef.current ? props.options.length - 1 : highlightedIndexRef.current - 1;
    setHighlightedIndex(index);
    setShowHighlight(true);
  };

  // Enter key event handler - select currently highlighted item if there is one
  const keyEnter = (event) => {
    if (highlightedIndexRef.current && props.options[highlightedIndexRef.current]) {
      selectOption(props.options[highlightedIndexRef.current]);
    } else {
      dispatch(
        serviceActions.genericAlert(
          'Could not select item',
          'Highlighted item could not be selected',
          AlertType.Warning
        )
      );
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        keyArrowDown(event);
        break;
      case 'ArrowUp':
        keyArrowUp(event);
        break;
      case 'Enter':
        keyEnter(event);
        break;
    }
  };

  const handleMouseEnter = () => {
    setShowHighlight(true);
  };

  const handleMouseLeave = () => {
    setHighlightedIndex(null);
    setShowHighlight(false);
  };

  const highlightOption = (index) => {
    setHighlightedIndex(index);
  };

  // Select the given option (option is an object - one of the entries from this.props.options)
  const selectOption = (option) => {
    props.onSelect?.(option);
  };

  const Option = props.optionClass;
  const options = props.options.map((option, index) => {
    // selectOption listens to bubbling up clicks to process select an item, however for
    // check boxes and radio buttons this may be a problem because React generates a click event when
    // their value changes which means these can be double processed.  Consequently the selectOption
    // function is also passed through to the option to allow it to be called directly and then
    // prevent the event bubbling up
    const selectThisOption = () => {
      if (props.listLabel === 'Filters' || !option.required) {
        // If 'required' is set prevent toggle of this column (Unless it's the Filters list, in which case it's permitted).
        selectOption(option);
      }
    };

    const optionElement = (
      <div
        data-test-tag={props.id}
        onMouseEnter={highlightOption.bind(null, index)}
        onClick={selectThisOption}
        key={`select${index}`}
      >
        <Option
          ref={(element) => (optionsRefs.current[`option-${index}`] = element)}
          index={index}
          option={option}
          highlight={props.showHighlight && showHighlight && highlightedIndex === index}
          selectOption={selectThisOption}
          descriptionField={props.descriptionField}
          data-testid={
            props['data-testid'] ? `${props['data-testid']}-option-${index}` : `${props.testid}-option-${index}`
          }
          dataid={dataid.current}
          deletable={props.deletable}
          onMutationComplete={props.onMutationComplete}
          onDeleteComplete={props.onDeleteComplete}
          glyph={props.glyph}
          listLabel={props.listLabel}
        />
      </div>
    );

    if (props.listLabel !== 'Filters' && option?.required) {
      return (
        <Tooltip relationship={'label'} content={'This column may not be disabled.'}>
          {optionElement}
        </Tooltip>
      );
    }

    return optionElement;
  });

  return (
    <div
      className={styles.listContainer}
      data-test-tag={props.id}
      ref={optionsContainerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {options}
    </div>
  );
};

SelectableList.propTypes = {
  options: PropTypes.array.isRequired,
  optionClass: PropTypes.any, //Function or Class Component
  showHighlight: PropTypes.bool,
  onSelect: PropTypes.func,
  onGlyph: PropTypes.func,
  deletable: PropTypes.bool,
  onDeleteComplete: PropTypes.func,
  onMutationComplete: PropTypes.func,
  glyph: PropTypes.any,
};

SelectableList.defaultProps = {
  optionClass: SelectableListOption,
  showHighlight: true,
};

SelectableList.contextTypes = {
  addKeyDownHandler: PropTypes.func,
  removeKeyDownHandler: PropTypes.func,
};

export default SelectableList;
